import { type Header, type Table, flexRender } from "@tanstack/react-table";
import cx from "classnames";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import {
	CaretDownIcon,
	ColumnDeleteIcon,
	GroupResourceIcon,
	SettingsIcon,
	SortAscendingIcon,
	SortDescendingIcon,
	ViewOffIcon,
} from "@moment/icons";

import { DropdownMenu } from "../../DropdownMenu";
import { InputCheckbox } from "../../InputCheckbox";
import { ColumnInsertLeft, ColumnInsertRight } from "../../icons";
import { type DataGridColumnDef } from "../useColumnState";
import {
	type ColumnType,
	ColumnTypeValues,
	disallowedInColumnHeaderSelection,
} from "../validation/parseColumnType";
import { ColumnTypeIcon } from "./ColumnTypeIcon";
import { ColumnTypeIconWithTooltip } from "./ColumnTypeIconWithTooltip";
import { EditColumnPopover } from "./EditColumnPopover";
import { columnSortLabels } from "./columnSortLabels";

export interface DataGridColumnHeaderProps<T> {
	headerID: string;
	table: Table<T>;
	header: Header<T, unknown>;
	columnDef: DataGridColumnDef<unknown, unknown>;
	inferredFieldType: ColumnType | undefined;
	isGrouped: boolean;
	isFirstCol: boolean;
	isLastCol: boolean;
	headerIndex: number;
	rowSelectBehavior?: "none" | "select" | "multiselect";
}
export function DataGridColumnHeader<T>({
	headerID,
	table,
	header,
	columnDef,
	inferredFieldType,
	isGrouped,
	isFirstCol,
	headerIndex,
	rowSelectBehavior,
}: DataGridColumnHeaderProps<T>) {
	const { meta } = table.options;
	const updateColumn = meta?.updateColumn;
	const isEditable = meta?.tableEditable && typeof updateColumn === "function";
	const [editingColumn, setEditingColumn] = useState(false);
	const width = header.getSize();
	const headerLabel = useMemo(
		() => flexRender(header.column.columnDef.header, header.getContext()),
		[header]
	);
	const headerLabelRef = useRef<HTMLSpanElement>(null);
	const columnTitleInputRef = useRef<HTMLInputElement>(null);
	const [headerLabelValue, setHeaderLabelValue] = useState(headerLabelRef.current?.innerText);

	const updateColumnSize = useCallback(() => {
		const columnDef = {
			...header.column.columnDef,
			size: header.column.getSize(),
		} as DataGridColumnDef<unknown, unknown>;
		updateColumn?.(headerID, columnDef);
	}, [header.column, headerID, updateColumn]);

	const onCloseEditColumnPopover = useCallback(() => setEditingColumn(false), [setEditingColumn]);

	//
	// NOTE: This "embedded" tooltip wrapper is to avoid the terribly complicated ref-forwarding
	// code we'd have to write to make the tooltip play well with the Radix dropdown menu. The hit
	// to clarity is just not worth the benefit. If you decide to pull this into a standalone
	// component, double check the tooltip actually works!
	//

	function dropdownMenuItem(dropdownMenuItemProps: {
		onSelect: ((event: Event) => void) | undefined;
		disabled: boolean;
		onDisabledTooltip: string;
		children: ReactNode;
	}) {
		const item = (
			<DropdownMenu.Item
				onSelect={dropdownMenuItemProps.onSelect}
				disabledMessage={dropdownMenuItemProps.onDisabledTooltip}
				disabled={dropdownMenuItemProps.disabled}
			>
				{dropdownMenuItemProps.children}
			</DropdownMenu.Item>
		);

		return item;
	}

	const { first, last } = columnSortLabels(inferredFieldType);

	const resolvedFieldType = columnDef.fieldType ?? inferredFieldType;

	const fieldTypeMenuItem = (
		<div className="flex items-center gap-1">
			<ColumnTypeIconWithTooltip fieldType={resolvedFieldType} />
			<div>Type: {resolvedFieldType}</div>
		</div>
	);

	const deleteMenuItem = (
		<div>
			<DropdownMenu.Item
				variant="negative"
				onSelect={() => updateColumn?.(headerID, null)}
				disabledMessage={
					!meta?.tableEditable
						? "Table is read-only"
						: "Column can't be deleted because column definitions were marked readonly."
				}
				disabled={!isEditable}
			>
				<ColumnDeleteIcon />
				<div>Delete Column</div>
			</DropdownMenu.Item>
		</div>
	);

	const insertColumnLeftMenuItem = (
		<div>
			<DropdownMenu.Item
				onSelect={() => updateColumn?.(headerID, null, "left")}
				disabledMessage={
					!meta?.tableEditable
						? "Table is read-only"
						: "Column can't be inserted because column definitions were marked readonly."
				}
				disabled={!isEditable}
			>
				<ColumnInsertLeft />
				<div>Insert Column Left</div>
			</DropdownMenu.Item>
		</div>
	);
	const insertColumnRightMenuItem = (
		<div>
			<DropdownMenu.Item
				onSelect={() => updateColumn?.(headerID, null, "right")}
				disabledMessage={
					!meta?.tableEditable
						? "Table is read-only"
						: "Column can't be inserted because column definitions were marked readonly."
				}
				disabled={!isEditable}
			>
				<ColumnInsertRight />
				<div>Insert Column Right</div>
			</DropdownMenu.Item>
		</div>
	);

	// These are complicated because we don't know whether desc or asc are the first in the sort
	// order chain.
	let resourceIcon: ReactNode;
	if (header.column.getIsGrouped()) {
		<GroupResourceIcon />;
	} else if (
		(header.column.getIsSorted() &&
			header.column.getFirstSortDir() === "asc" &&
			header.column.getNextSortingOrder() === "desc") ||
		(header.column.getFirstSortDir() === "desc" &&
			header.column.getNextSortingOrder() === false)
	) {
		resourceIcon = <SortAscendingIcon />;
	} else if (header.column.getIsSorted()) {
		resourceIcon = <SortDescendingIcon />;
	} else {
		resourceIcon = <ColumnTypeIcon className="" fieldType={resolvedFieldType} />;
	}

	const trigger = (
		<button
			className={cx(
				"z-surface",
				"group",
				"flex",
				"items-center",
				"justify-between",
				"h-10",
				"w-full",
				"font-normal",
				"text-sm",
				"text-left",
				"py-2",
				"hover:bg-button-normal-secondary-hover",
				"pr-2",
				{ "pl-6": isFirstCol && isGrouped },
				{ "pl-2": !isFirstCol || !isGrouped },
				{
					"text-palette-brand-11 font-semibold":
						header.column.getIsGrouped() || header.column.getIsSorted(),
					"text-secondary": !header.column.getIsGrouped() && !header.column.getIsSorted(),
				},
				"@container/datagridcolumnheader"
			)}
		>
			<div className="mr-1 flex items-center space-x-1 truncate">
				{(rowSelectBehavior === "multiselect" || rowSelectBehavior === "select") &&
					headerIndex === 0 && (
						<div className={cx("mr-2 flex items-center")}>
							{rowSelectBehavior === "multiselect" && (
								<InputCheckbox
									aria-label="Select all rows"
									size="sm"
									isSelected={table.getIsAllRowsSelected()}
									isIndeterminate={table.getIsSomeRowsSelected()}
									onPointerDown={(e) => {
										// IMPORTANT: Prevent triggering Radix
										// DropdownMenu.
										e.stopPropagation();
										e.preventDefault();
									}}
									onChange={() => {
										if (table.getIsSomeRowsSelected()) {
											table.toggleAllRowsSelected(false);
										} else {
											table.toggleAllRowsSelected();
										}
									}}
								/>
							)}
							{rowSelectBehavior === "select" && <div className="h-4 w-4" />}
						</div>
					)}
				<div className="hidden @[6rem]/datagridcolumnheader:block">{resourceIcon}</div>
				<span
					className="truncate"
					title={`${headerLabelValue} (type: \u201C${resolvedFieldType}\u201D)`}
					ref={headerLabelRef}
				>
					{headerLabel}
				</span>
			</div>
			<div>
				<CaretDownIcon
					size={16}
					className="fill-icon-default group-hover:fill-icon-hover"
				/>
			</div>
		</button>
	);

	let menu = header.isPlaceholder ? null : (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="w-56 text-xs" align="start">
					{/* Editable Name */}
					{dropdownMenuItem({
						onSelect: () => setEditingColumn(true),
						disabled: !isEditable,
						onDisabledTooltip: !meta?.tableEditable
							? "Table is read-only"
							: "Column is not editable",
						children: (
							<>
								<SettingsIcon />
								<div>Edit column</div>
							</>
						),
					})}
					{/* {isEditable && (
							<div className="p-3 pt-1">
								<InputText
									ref={columnTitleInputRef}
									size="sm"
									fullWidth
									value={internalHeaderValue}
									onChange={(e) => setInternalHeaderValue(e.target.value)}
									onBlur={updateColumnTitle}
									onKeyDown={(e) => {
										e.stopPropagation();
										if (e.key === "Enter") {
											updateColumnTitle();
										} else if (e.key === "Escape") {
											setInternalHeaderValue(headerLabelValue);
											// TODO blur input, currently it commits the change because of onBlur
											// columnTitleInputRef.current?.blur();
										}
									}}
								/>
							</div>
						)} */}
					<DropdownMenu.Sub>
						<DropdownMenu.SubTrigger
							asChild={false}
							disabledMessage={
								!meta?.tableEditable
									? "Table is read-only"
									: "Type can't be changed because column definitions were marked readonly."
							}
							disabled={!isEditable}
						>
							{fieldTypeMenuItem}
						</DropdownMenu.SubTrigger>
						<DropdownMenu.Portal>
							<DropdownMenu.SubContent className="w-80">
								<DropdownMenu.RadioGroup value={columnDef.fieldType}>
									{ColumnTypeValues.map((fieldTypeItem) => {
										if (disallowedInColumnHeaderSelection.has(fieldTypeItem)) {
											return null;
										}

										return (
											<DropdownMenu.RadioItem
												key={fieldTypeItem}
												value={fieldTypeItem}
												onSelect={() => {
													updateColumn?.(headerID, {
														...header.column.columnDef,
														fieldType: fieldTypeItem,
													} as DataGridColumnDef<unknown, unknown>);
												}}
											>
												<div className="flex flex-1 items-center">
													<div className="flex items-center gap-1">
														<ColumnTypeIconWithTooltip
															fieldType={fieldTypeItem}
														/>

														{fieldTypeItem}
													</div>
												</div>
											</DropdownMenu.RadioItem>
										);
									})}
								</DropdownMenu.RadioGroup>
							</DropdownMenu.SubContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Sub>
					<DropdownMenu.Separator />
					{dropdownMenuItem({
						onSelect: () => header.column.toggleSorting(false),
						disabled: !isEditable || !header.column.getCanSort(),
						onDisabledTooltip: !isEditable
							? "Table is read-only"
							: "Column does not contain sortable data",
						children: (
							<>
								<SortAscendingIcon />
								<div className="flex items-center gap-1">
									Sort {first} → {last}
								</div>
							</>
						),
					})}
					{dropdownMenuItem({
						onSelect: () => header.column.toggleSorting(true),
						disabled: !isEditable || !header.column.getCanSort(),
						onDisabledTooltip: !isEditable
							? "Table is read-only"
							: "Column does not contain sortable data",
						children: (
							<>
								<SortDescendingIcon />
								<div className="flex items-center gap-1">
									Sort {last} → {first}
								</div>
							</>
						),
					})}
					{dropdownMenuItem({
						onSelect: () => header.column.toggleGrouping(),
						disabled: !isEditable || !header.column.getCanGroup(),
						onDisabledTooltip: !isEditable
							? "Table is read-only"
							: "Column does not contain groupable data",
						children: (
							<>
								<GroupResourceIcon />
								<div>Group by this column</div>
							</>
						),
					})}
					<DropdownMenu.Item
						onSelect={header.column.getToggleVisibilityHandler()}
						disabled={!isEditable || !header.column.getCanHide()}
					>
						<ViewOffIcon />
						<div>Hide column</div>
					</DropdownMenu.Item>
					<DropdownMenu.Separator />
					{/* TODO: Link to docs on how to make column definitions editable */}
					{insertColumnLeftMenuItem}
					{/* TODO: Link to docs on how to make column definitions editable */}
					{insertColumnRightMenuItem}
					<DropdownMenu.Separator />
					{/* TODO: Link to docs on how to make column definitions editable */}
					{deleteMenuItem}
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);

	if (editingColumn) {
		menu = (
			<EditColumnPopover<T>
				table={table}
				columnDef={columnDef}
				inferredFieldType={resolvedFieldType}
				headerID={headerID}
				header={header}
				headerLabel={headerLabel}
				headerLabelRef={headerLabelRef}
				headerLabelValue={headerLabelValue}
				setHeaderLabelValue={setHeaderLabelValue}
				columnTitleInputRef={columnTitleInputRef}
				onClose={onCloseEditColumnPopover}
			>
				{trigger}
			</EditColumnPopover>
		);
	}

	return (
		<th className="relative p-0" key={header.id} style={{ width }} colSpan={header.colSpan}>
			{menu}
			<div
				role="button"
				tabIndex={-1}
				// cursor-col-resize is definitely a tailwind class https://tailwindcss.com/docs/cursor
				className="cursor-col-resize group absolute right-0 top-0 flex h-full touch-none select-none flex-row items-center"
				onMouseDown={header.getResizeHandler()}
				onMouseUp={updateColumnSize}
				onTouchStart={header.getResizeHandler()}
				onTouchEnd={updateColumnSize}
				style={{ width: 6, marginRight: -3, cursor: "col-resize" }}
			>
				<div
					className={cx("z-surface h-5/6 w-full rounded-full hover:bg-brand", {
						"bg-brand": header.column.getIsResizing(),
					})}
				/>
			</div>
		</th>
	);
}
